import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, useAdminState, Erro } from "../../hooks/useAdminApi";

/**
 * WEBHOOKS.
 *
 * O `/webhooks/contract` antigo respondia 400 para todo evento que não fosse
 * pago/cancelado — ou seja, aguardando pagamento, recusado, reembolso e renovação eram
 * DESCARTADOS sem ninguém nunca ter visto o formato deles.
 *
 * O capturador grava tudo. Esta tela mostra, POR EVENTO, o mapa de campos do JSON —
 * é com ela que se escreve o handler de cada um, em cima do dado real em vez de chute.
 */

export default function Webhooks() {
  const { tema, setTema } = useOutletContext();
  const { erro, rodar, limparErro } = useAdminState();

  const [origem, setOrigem] = useState("greenn");
  const [dados, setDados] = useState(null);
  const [aberto, setAberto] = useState(null);

  useEffect(() => {
    setDados(null);
    setAberto(null);
    rodar(async () => setDados(await apiGet(API_ROUTES.ADMIN.webhookSchema(origem))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origem]);

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Webhooks"
        subtitulo="Tudo que a Greenn e as lojas mandam — inclusive os eventos que ainda não sabemos tratar."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
          {[["greenn", "Greenn"], ["revenuecat", "App Store / Play Store"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setOrigem(k)}
              className={origem === k ? "adm__btn" : "adm__btnGhost"}
            >
              {l}
            </button>
          ))}
        </div>

        {!dados && <p className="adm__vazio">Carregando…</p>}

        {dados && dados.totalCapturado === 0 && (
          <div className="adm__card">
            <p className="adm__vazio">
              Nenhum evento capturado ainda.
              <br />
              <span style={{ fontSize: 12.5 }}>
                Cadastre a URL do capturador na {origem === "greenn" ? "Greenn" : "sua conta do RevenueCat"} —
                assim que os eventos começarem a chegar, o formato de cada um aparece aqui.
              </span>
            </p>
          </div>
        )}

        {dados && dados.semHandler?.length > 0 && (
          <div
            style={{
              background: "rgba(255,140,0,.10)",
              border: "1px solid rgba(255,140,0,.3)",
              borderRadius: 12,
              padding: "13px 16px",
              fontSize: 13.5,
              marginBottom: 18,
              lineHeight: 1.6,
              color: "var(--orange)",
            }}
          >
            <strong>
              {dados.semHandler.length}{" "}
              {dados.semHandler.length === 1 ? "evento ainda sem tratamento" : "eventos ainda sem tratamento"}
            </strong>
            : {dados.semHandler.join(", ")}.
            <br />
            <span style={{ color: "var(--text2)" }}>
              Estes chegam e são gravados, mas ninguém age sobre eles. É a fila de trabalho.
            </span>
          </div>
        )}

        {dados?.tipos?.map((t) => (
          <div key={t.evento} className="adm__card" style={{ marginBottom: 10, overflow: "hidden" }}>
            <button
              onClick={() => setAberto(aberto === t.evento ? null : t.evento)}
              style={{
                width: "100%",
                background: "none",
                border: "none",
                padding: 16,
                display: "flex",
                alignItems: "center",
                gap: 12,
                cursor: "pointer",
                font: "inherit",
                color: "inherit",
                textAlign: "left",
              }}
            >
              <span style={{ color: "var(--text3)", fontSize: 11 }}>
                {aberto === t.evento ? "▼" : "▶"}
              </span>
              <code className="adm__code" style={{ fontSize: 12.5 }}>
                {t.evento}
              </code>
              {t.temHandler ? (
                <span className="adm__chip adm__chip--green">tratado</span>
              ) : (
                <span className="adm__chip adm__chip--orange">sem handler</span>
              )}
              <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--text2)" }}>
                {t.total} {t.total === 1 ? "evento" : "eventos"} · último{" "}
                {new Date(t.ultimo).toLocaleString("pt-BR")}
              </span>
            </button>

            {aberto === t.evento && (
              <div style={{ borderTop: "1px solid var(--border)", padding: 16 }}>
                <h4 style={{ fontSize: 11, letterSpacing: 0.8, color: "var(--text3)", margin: "0 0 10px", textTransform: "uppercase" }}>
                  Mapa de campos
                </h4>
                <p style={{ fontSize: 12.5, color: "var(--text2)", margin: "0 0 12px", lineHeight: 1.6 }}>
                  Cada campo do JSON, o tipo, e <strong>em quantos % dos eventos ele aparece</strong>.
                  Um campo que só vem em 60% é um campo que você <strong>não pode</strong> assumir
                  como garantido.
                </p>

                <table className="adm__tabela">
                  <thead>
                    <tr>
                      <th>Campo</th>
                      <th style={{ width: 110 }}>Tipo</th>
                      <th style={{ width: 100 }}>Presente em</th>
                      <th>Exemplo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.campos.map((c) => {
                      const parcial = c.presenteEm !== "100%";
                      return (
                        <tr key={c.campo}>
                          <td>
                            <code className="adm__code">{c.campo}</code>
                          </td>
                          <td style={{ color: "var(--text2)" }}>{c.tipo}</td>
                          <td style={{ color: parcial ? "var(--orange)" : "var(--green)", fontWeight: parcial ? 600 : 400 }}>
                            {c.presenteEm}
                          </td>
                          <td style={{ color: "var(--text2)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {typeof c.exemplo === "object" ? JSON.stringify(c.exemplo) : String(c.exemplo)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <details style={{ marginTop: 16 }}>
                  <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--text2)" }}>
                    Ver o JSON cru de um exemplo
                  </summary>
                  <pre
                    style={{
                      background: "var(--bg)",
                      border: "1px solid var(--border)",
                      borderRadius: 10,
                      padding: 14,
                      fontSize: 11.5,
                      overflowX: "auto",
                      marginTop: 10,
                      fontFamily: '"Roboto Mono", monospace',
                    }}
                  >
                    {JSON.stringify(t.exemplo, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
